<mat-toolbar [color]="getColour()" class="toolbar">
    <nav class="step-menu" aria-label="Onboarding steps">
        @for (s of steps; track s.label; let i = $index) {
            <button mat-button [class.active]="step === i + 1" [attr.aria-current]="step === i + 1 ? 'step' : null" (click)="setStep(i + 1)">{{s.label}}</button>
        }
    </nav>
    <span class="step-count">Step {{step}} of {{steps.length}}</span>
    <span class="spacer"></span>
    <button mat-icon-button (click)="closePanel()" aria-label="Close the guide" matTooltip="Close">
        <mat-icon>close</mat-icon>
    </button>
</mat-toolbar>
<div class="onboarding-container" tabindex="-1">
    <div class="actions-container">
        <button mat-stroked-button (click)="setStep(step-1)" [disabled]="step===1"><mat-icon>skip_previous</mat-icon>Previous</button>
        <mat-progress-bar mode="determinate" [value]="100 * step / steps.length" aria-hidden="true"></mat-progress-bar>
        @if (step < steps.length) {
            <button mat-flat-button color="accent" (click)="setStep(step+1)">Next<mat-icon iconPositionEnd>skip_next</mat-icon></button>
        }@else {
            <button mat-flat-button color="accent" (click)="closePanel()">Finish</button>
        }
    </div>
    @if (step===1) {
        <div>
            <h2>{{current.title}}</h2>
            <div>
                <p>Welcome to your LINQ onboarding experience.</p>
                <p>The help menu (top right) has help guides you can search, videos and tutorials, and Ask LINQ AI for questions. You can toggle on screen hints using the round help button (<mat-icon>help</mat-icon>) at the bottom right of the screen.</p>
                <p>Play the video below to watch the introduction.</p>
                <p>Use Next to move through this guide, or the arrow keys. You can also jump to a section using the menu at the top.</p>
            </div>
        </div>
    }
    @if (step===2) {
        <div>
            <h2>{{current.title}}</h2>
            <div>
                <p>An organisation is the container for your models. You can invite other users to join your organisation.</p>
                @if (getOrganisation()) {
                    <p>You are currently working within the organisation {{getOrganisation().name}}. If you have access to more than one organisation, choose Organisations (top right) to switch between them.</p>
                }@else {
                    <p>You are currently viewing a list of organisations that you have access to. The demo organisation is an organisation created by LINQ to help you learn LINQ's capabilities.</p>
                    <p>To create your own, choose Create organisation. You name it and pick its industry, choose one process to start with, and tell AI about that process: it describes the process for you to check, then builds your first model. It takes a few minutes.</p>
                }
                <p>Models are defined within workspaces. Every organisation starts with a current state workspace which is used to model the current state of processes within the organisation.</p>
                <p>You can create project workspaces that are used to model a future state or change to the organisation. You can compare project workspaces with the current state workspace to see the impact of change.</p>

                <p>The organisation home page provides a dashboard for the selected workspace within an organisation. The following video describes the home page.</p>

            </div>
        </div>
    }
    @if (step===3) {
        <div>
            <h2>{{current.title}}</h2>
            <div>
                <p>LINQ has been built on a simple, powerful and structured language. This language enables us to create connections between the things which make up what an organisation is. The 4 elements we need to capture and connect are:</p>
                <ul>
                    <li><b>Information</b> - the content which powers our actions, and in turn is created from the work that goes on.</li>
                    <li><b>Actions</b> - the transactions or processes which take place, performed by roles or automated by systems.</li>
                    <li><b>Systems</b> - the tools which the business uses. This could be a piece of technology, but could equally be a piece of paper.</li>
                    <li><b>Roles</b> - the people who do the work. People carry a cost and have a capacity.</li>
                </ul>

                <p>We have created a 1-page guide to help you remember this language. <a href="https://www.thinklikelinq.com/wp-content/uploads/2025/07/Capture-Checklist.pdf" target="_blank" rel="noopener">Download the capture checklist (PDF).</a> Keep it handy as it will help you think about the content you need to build a model of your business.</p>
            </div>
        </div>
    }
    @if (step===4) {
        <div>
            <h2>{{current.title}}</h2>
            <div>
                <p>Models utilise the LINQ language to describe a business function within your organisation.</p>
                <p>To create a model, choose Create model on the home page's Models card, or on the Models menu at the top.</p>
                <p>You can have AI build a first draft: tell it about the process, and add a document or diagram if you have one. AI describes the process for you to check and correct, then builds the model. Or start with a blank model and build it yourself from the palette.</p>
                <p>We recommend that you take the 'creating your first model' tutorial.</p>
                <ng-container *ngTemplateOutlet="tutorialLink"></ng-container>
            </div>
        </div>
    }
    @if (step===5) {
        <div>
            <h2>{{current.title}}</h2>
            <div>
                <p>Quantifying a model involves adding load (frequency at which data, information, items are processed) and duration (time taken to process data, information, items).</p>
                <p>This allows you to gain insights into time, cost, value and the allocation of carbon.</p>
                <p>A tutorial on setting frequency and duration is provided.</p>
                <ng-container *ngTemplateOutlet="tutorialLink"></ng-container>
            </div>
        </div>
    }
    @if (step===6) {
        <div>
            <h2>{{current.title}}</h2>
            <div>
                <p>Adding roles and systems to your model provides an understanding of staff workload and the systems required.</p>
                <p>Adding costs to roles and systems provides allocation of costs to work and therefore where efficiencies may be identified.</p>
            </div>
        </div>
    }
    @if (step===7) {
        <div>
            <h2>{{current.title}}</h2>
            <div>
                <p>Insights provide an analytical view of models (processes) within the organisation. Insights can guide you to areas for improvement and are used to compare the impact of change.</p>
                <p>See them for a model in the insights panel on the model screen, and across models on the Analysis menu (Analysis › Insights), which also has the EPM scorecard and simulation.</p>
                <p><a href="#/help/model-insights" (click)="closePanel()">Read the help guide on insights</a></p>
                <p>The following shows insights for our demo organisation. It collates the insights from each of the models.</p>
            </div>
        </div>
    }
    @if (step===8) {
        <div>
            <h2>{{current.title}}</h2>
            <div>
                <p>Projects are used to model changes within an organisation. You can compare these changes with the current state to understand impact and develop a business case for the change.</p>
                <p>When the change is made for real, an admin merges the project into the current state, keeping your current state models in step with your organisation.</p>
                <p><a href="#/help/projects" (click)="closePanel()">Read the help guide on projects</a></p>
                <p>The following shows a project model and its insights compared to the current state of the same model or process.</p>
            </div>
        </div>
    }
    @if (step===9) {
        <div>
            <h2>{{current.title}}</h2>
        </div>
        <div>
            <p>In your organisation list, you will also see the Demo Org. This is an organisation we have created which will help you to learn LINQ's capabilities.</p>
            <p>You can open the organisation and look at any of the models and insights. We make reference to the Demo Org and its content through our training material and in-application guides.</p>
            <p>As we release new features into LINQ, we will add more content into the Demo Org, so check back in from time to time.</p>
        </div>
    }
    @if (step===10) {
        <div>
            <h2>{{current.title}}</h2>
        </div>
        <div>
            <p>Each organisation is billed monthly based on number of users, projects and nodes. Invited users aren't counted until they accept. It is free for {{FREE_USERS}} {{FREE_USERS === 1 ? 'user' : 'users'}}, {{FREE_PROJECTS}} {{FREE_PROJECTS === 1 ? 'project' : 'projects'}} and up to {{FREE_NODES}} nodes.</p>
            <p>Anything above these limits gets billed at a rate of {{currency.userPrice}} per user, {{currency.projectPrice}} per project and {{currency.nodePrice}} per node.</p>
            <p>Billing occurs at the beginning of each month with charges based on usage at the end of the previous month.</p>
            <p>Admins can view the organisation's previous invoices, current usage and a prediction of next month's under Statistics &amp; Billing on the organisation menu (the organisation's name, top left).</p>
            <p>The billing email address will default to your first user's email address. You can set a different email by editing the organisation details. You can also set an invoice reference here.</p>
        </div>
    }

    @if (imageUrl) {
        <div class="image-container">
            <img [src]="imageUrl" [alt]="'Example of ' + current.title" loading="lazy"/>
        </div>
    }
    @if (videoUrl) {
        <div class="video-container">
            <app-video [videoUrl]="videoUrl"></app-video>
        </div>
    }
</div>

<ng-template #tutorialLink>
    @if (getOrganisation()) {
        <button mat-stroked-button color="accent" (click)="gotoTutorial()"><mat-icon>school</mat-icon>Open the tutorial</button>
    }@else {
        <p class="note">Open one of your organisations to take the tutorial. It's in the help menu there.</p>
    }
</ng-template>
